import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  BanknotesIcon,
  ClockIcon,
  PhoneIcon,
  QrCodeIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline'
import { reconciliationChatAPI, whatsappAPI, ReconciliationChatResponse } from '@/services/api'

interface ChatMessage {
  id: string
  content: string
  sender: 'user' | 'bot'
  timestamp: Date
  intent?: string
  confidence?: number
  actions?: Array<{ type: string; label: string }>
  suggestions?: string[]
  data?: any
  ai_provider?: string
}

const SmartReconciliationPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [pendingCount, setPendingCount] = useState(0)
  const [whatsappStatus, setWhatsappStatus] = useState<any>(null)
  const [showWhatsappModal, setShowWhatsappModal] = useState(false)
  const [whatsappPhone, setWhatsappPhone] = useState('')
  const [activeTab, setActiveTab] = useState<'chat' | 'stats' | 'whatsapp'>('chat')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Carregar sugestoes iniciais
  const loadSuggestions = useCallback(async () => {
    try {
      const data = await reconciliationChatAPI.getSuggestions()
      setSuggestions(data.suggestions || [])
      setPendingCount(data.pending_count || 0)
    } catch (err) {
      console.error('Erro ao carregar sugestoes:', err)
    }
  }, [])

  // Carregar status do WhatsApp
  const loadWhatsappStatus = useCallback(async () => {
    try {
      const status = await whatsappAPI.getStatus()
      setWhatsappStatus(status)
    } catch (err) {
      console.error('Erro ao carregar status WhatsApp:', err)
    }
  }, [])

  useEffect(() => {
    loadSuggestions()
    loadWhatsappStatus()

    // Mensagem de boas-vindas
    const welcomeMessage: ChatMessage = {
      id: 'welcome',
      content: `Ola! Sou seu assistente de conciliacao bancaria com IA.

Posso ajudar voce a:
- Ver transacoes pendentes de conciliacao
- Conciliar automaticamente com inteligencia artificial
- Classificar transacoes por categoria
- Identificar transacoes suspeitas ou duplicadas

O que voce gostaria de fazer?`,
      sender: 'bot',
      timestamp: new Date(),
      suggestions: [
        'Mostrar transacoes pendentes',
        'Conciliar automaticamente',
        'Ver estatisticas'
      ]
    }
    setMessages([welcomeMessage])
  }, [loadSuggestions, loadWhatsappStatus])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendMessage = async (message?: string) => {
    const messageToSend = message || inputMessage.trim()
    if (!messageToSend || loading) return

    // Adicionar mensagem do usuario
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content: messageToSend,
      sender: 'user',
      timestamp: new Date()
    }
    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setLoading(true)

    try {
      const response = await reconciliationChatAPI.sendMessage(messageToSend)

      const botMessage: ChatMessage = {
        id: `bot-${Date.now()}`,
        content: response.response,
        sender: 'bot',
        timestamp: new Date(),
        intent: response.intent,
        confidence: response.confidence,
        actions: response.actions,
        suggestions: response.suggestions,
        data: response.data,
        ai_provider: response.ai_provider
      }
      setMessages(prev => [...prev, botMessage])

      // Atualizar sugestoes
      loadSuggestions()

    } catch (err: any) {
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        content: `Desculpe, ocorreu um erro: ${err.message || 'Erro desconhecido'}`,
        sender: 'bot',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleQuickAction = async (action: string) => {
    setLoading(true)
    try {
      const response = await reconciliationChatAPI.quickAction(action)

      const botMessage: ChatMessage = {
        id: `action-${Date.now()}`,
        content: response.response,
        sender: 'bot',
        timestamp: new Date(),
        data: response.data
      }
      setMessages(prev => [...prev, botMessage])
      loadSuggestions()
    } catch (err: any) {
      console.error('Erro na acao rapida:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleLinkWhatsapp = async () => {
    if (!whatsappPhone) return

    try {
      await whatsappAPI.linkPhone(whatsappPhone)
      setShowWhatsappModal(false)
      setWhatsappPhone('')
      loadWhatsappStatus()
    } catch (err: any) {
      console.error('Erro ao vincular WhatsApp:', err)
    }
  }

  const getProviderBadge = (provider?: string) => {
    if (!provider) return null

    const badges: Record<string, { color: string; label: string }> = {
      'gemini': { color: 'bg-blue-100 text-blue-800', label: 'Gemini' },
      'claude': { color: 'bg-purple-100 text-purple-800', label: 'Claude' },
      'openai': { color: 'bg-green-100 text-green-800', label: 'GPT' },
      'rules': { color: 'bg-gray-100 text-gray-700', label: 'Regras' }
    }

    const badge = badges[provider.toLowerCase()] || badges['rules']
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${badge.color}`}>
        {badge.label}
      </span>
    )
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <SparklesIcon className="w-8 h-8 text-purple-600" />
              Conciliacao Inteligente
            </h1>
            <p className="text-gray-600 mt-1">
              Concilie transacoes via chat com IA ou WhatsApp
            </p>
          </div>
          <div className="flex items-center gap-4">
            {pendingCount > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-100 rounded-lg">
                <ClockIcon className="w-5 h-5 text-yellow-600" />
                <span className="text-yellow-800 font-medium">{pendingCount} pendentes</span>
              </div>
            )}
            <button
              onClick={() => setShowWhatsappModal(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                whatsappStatus?.linked
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <PhoneIcon className="w-5 h-5" />
              {whatsappStatus?.linked ? 'WhatsApp Conectado' : 'Conectar WhatsApp'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('chat')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'chat'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="w-5 h-5" />
            Chat
          </div>
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'stats'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5" />
            Estatisticas
          </div>
        </button>
        <button
          onClick={() => setActiveTab('whatsapp')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'whatsapp'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <PhoneIcon className="w-5 h-5" />
            WhatsApp
          </div>
        </button>
      </div>

      {/* Chat Tab */}
      {activeTab === 'chat' && (
        <div className="flex-1 flex gap-4">
          {/* Chat Area */}
          <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                      message.sender === 'user'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="whitespace-pre-wrap text-sm">{message.content}</div>

                    {/* Actions */}
                    {message.actions && message.actions.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {message.actions.map((action, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleQuickAction(action.type)}
                            className="px-3 py-1 bg-white text-purple-600 rounded-full text-sm font-medium hover:bg-purple-50 transition-colors"
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Suggestions */}
                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {message.suggestions.map((sug, idx) => (
                          <button
                            key={idx}
                            onClick={() => sendMessage(sug)}
                            className="block w-full text-left px-3 py-1.5 bg-white/80 hover:bg-white rounded-lg text-sm text-gray-700 transition-colors"
                          >
                            {sug}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="mt-2 flex items-center justify-between text-xs opacity-70">
                      <span>{formatTime(message.timestamp)}</span>
                      {message.sender === 'bot' && message.ai_provider && (
                        <span>{getProviderBadge(message.ai_provider)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2 text-gray-600">
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Processando...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Digite um comando ou pergunta..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={loading}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!inputMessage.trim() || loading}
                  className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar - Quick Actions */}
          <div className="w-80 space-y-4">
            {/* Status Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <BanknotesIcon className="w-5 h-5 text-purple-600" />
                Status
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Pendentes</span>
                  <span className="font-medium text-yellow-600">{pendingCount}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Acoes Rapidas</h3>
              <div className="space-y-2">
                <button
                  onClick={() => handleQuickAction('list_pending')}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-2"
                >
                  <ClockIcon className="w-4 h-4 text-yellow-600" />
                  Ver Pendentes
                </button>
                <button
                  onClick={() => handleQuickAction('auto_reconcile')}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-2"
                >
                  <SparklesIcon className="w-4 h-4 text-purple-600" />
                  Conciliar Automatico
                </button>
                <button
                  onClick={() => handleQuickAction('stats')}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-2"
                >
                  <ChartBarIcon className="w-4 h-4 text-blue-600" />
                  Ver Estatisticas
                </button>
                <button
                  onClick={() => handleQuickAction('suspicious')}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-2"
                >
                  <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
                  Ver Suspeitas
                </button>
              </div>
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-4">
                <h3 className="font-semibold text-purple-900 mb-3">Sugestoes</h3>
                <div className="space-y-2">
                  {suggestions.map((sug, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickAction(sug.action)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        sug.priority === 'high'
                          ? 'bg-purple-100 hover:bg-purple-200 text-purple-900'
                          : 'bg-white hover:bg-purple-50 text-gray-700'
                      }`}
                    >
                      {sug.text}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4">Estatisticas de Conciliacao</h2>
          <p className="text-gray-600">Digite "estatisticas" no chat para ver dados detalhados.</p>
        </div>
      )}

      {/* WhatsApp Tab */}
      {activeTab === 'whatsapp' && (
        <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PhoneIcon className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Conciliacao via WhatsApp</h2>
              <p className="text-gray-600">
                Vincule seu WhatsApp para receber notificacoes e conciliar transacoes por mensagem.
              </p>
            </div>

            {whatsappStatus?.linked ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <CheckCircleIcon className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <h3 className="font-semibold text-green-900 mb-1">WhatsApp Conectado!</h3>
                <p className="text-green-700 mb-4">Numero: {whatsappStatus.phone}</p>
                <div className="space-y-2 text-sm text-green-800">
                  <p>Voce pode enviar comandos como:</p>
                  <ul className="space-y-1">
                    <li>- "pendentes" para ver transacoes</li>
                    <li>- "conciliar" para auto-match</li>
                    <li>- "estatisticas" para ver resumo</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold mb-4">Vincular WhatsApp</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Numero do WhatsApp
                      </label>
                      <input
                        type="tel"
                        value={whatsappPhone}
                        onChange={(e) => setWhatsappPhone(e.target.value)}
                        placeholder="5511999999999"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Formato: DDI + DDD + Numero (ex: 5511999999999)
                      </p>
                    </div>
                    <button
                      onClick={handleLinkWhatsapp}
                      disabled={!whatsappPhone}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      Vincular WhatsApp
                    </button>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-xl p-6">
                  <h3 className="font-semibold text-purple-900 mb-3">Como Funciona?</h3>
                  <ol className="space-y-2 text-sm text-purple-800">
                    <li>1. Vincule seu numero de WhatsApp</li>
                    <li>2. Receba notificacoes de transacoes pendentes</li>
                    <li>3. Envie comandos por mensagem para conciliar</li>
                    <li>4. Confirme ou rejeite matches sugeridos pela IA</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Link Modal */}
      {showWhatsappModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Conectar WhatsApp</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numero do WhatsApp
                </label>
                <input
                  type="tel"
                  value={whatsappPhone}
                  onChange={(e) => setWhatsappPhone(e.target.value)}
                  placeholder="5511999999999"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowWhatsappModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleLinkWhatsapp}
                  disabled={!whatsappPhone}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Vincular
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SmartReconciliationPage
